/** Cantidad kardex: 3 decimales (igual que backend ReportController::q3). */
export const q3 = (value) => Math.round(Number(value || 0) * 1000) / 1000;

/** Monto kardex: 4 decimales (igual que backend ReportController::q4). */
export const q4 = (value) => Math.round(Number(value || 0) * 10000) / 10000;

/** Costo promedio a partir de existencias acumuladas. */
export const kardexAverageCost = (stock, totalValue) => {
  const s = q3(stock);
  if (Math.abs(s) <= TOLERANCE_ZERO) return 0;
  return q4(totalValue / s);
};

/** Costo promedio en saldos tras un movimiento (igual que ReportController). */
export const resolveKardexAverageCostAfter = (movement, stock, totalValue) => {
  if (movement?.kardex_event === 'ajuste_costo' && movement?.average_cost_after != null) {
    const dbCost = q4(movement.average_cost_after);
    if (dbCost > 0) return dbCost;
  }
  return Math.abs(q3(stock)) > TOLERANCE_ZERO ? q4(totalValue / stock) : 0;
};

/** Valor total en saldos tras ajuste de costo. */
export const syncKardexTotalValueAfterCostAdjustment = (movement, stock, averageCostAfter, totalValue) => {
  if (
    movement?.kardex_event === 'ajuste_costo'
    && averageCostAfter > 0
    && Math.abs(q3(stock)) > TOLERANCE_ZERO
  ) {
    return q4(averageCostAfter * q3(stock));
  }
  return totalValue;
};

/** Al vaciar stock: costo promedio y total exacto pendiente (misma regla que backend). */
export function kardexSalidaTotalsWhenZeroingStock(
  stockBefore,
  totalValueBefore,
  quantity,
  averageCostBefore,
  lastAverageCost = 0
) {
  const qty = q3(quantity);
  const stockAfter = q3(stockBefore - qty);
  if (Math.abs(stockAfter) > TOLERANCE_ZERO) return null;

  const exitUnit = kardexSalidaUnitPrice(averageCostBefore, lastAverageCost);
  if (exitUnit <= 0) return null;

  const lineTotal = kardexLineTotal(quantity, exitUnit);
  if (Math.abs(q4(totalValueBefore) - lineTotal) <= TOLERANCE_ZERO) return null;

  return { unit_price: exitUnit, total: q4(totalValueBefore) };
}

/** Costo unitario de salida desde saldos (no el precio histórico de compra). */
export const kardexSalidaUnitPrice = (averageCostBefore, lastAverageCost = 0) => {
  const basis = Math.abs(averageCostBefore) > TOLERANCE_ZERO ? averageCostBefore : lastAverageCost;
  return basis > 0 ? kardexExitUnitPrice(basis) : 0;
};

/** Costo unitario de salida: promedio anterior redondeado a 4 decimales. */
export const kardexExitUnitPrice = (averageCostBefore) => q4(averageCostBefore);

/** Total de línea: cantidad × costo unitario a 4 decimales. */
export const kardexLineTotal = (quantity, unitPrice) => q4(q3(quantity) * q4(unitPrice));

/**
 * Total coherente a 4 decimales; tolera polvo de redondeo menor a S/ 0.05 en la línea.
 */
export function isKardexLineTotalConsistent(quantity, unitPrice, total) {
  const qty = q3(quantity);
  const unit = q4(unitPrice);
  const lineTotal = q4(total);
  if (qty <= 0) return true;
  if (q4(lineTotal / qty) === unit) return true;
  if (q4(kardexLineTotal(qty, unit)) === lineTotal) return true;
  return Math.abs(lineTotal - q4(kardexLineTotal(qty, unit))) < LINE_TOTAL_DUST_TOLERANCE;
}

const TOLERANCE_ZERO = 0.00009;

/** Residuo monetario tolerable cuando el stock ya está en cero (polvo de redondeo). */
export const ZERO_STOCK_DUST_TOLERANCE = 0.05;

/** Diferencia cantidad×costo vs total tolerable en una línea (polvo de redondeo en compras). */
export const LINE_TOTAL_DUST_TOLERANCE = 0.01;

/**
 * Normaliza la configuración «contar inconsistencias desde» del API o module settings.
 */
export function normalizeKardexInconsistencyCountFrom(raw) {
  if (!raw?.enabled || !raw?.date) return null;
  let time = raw.time ? String(raw.time) : '00:00:00';
  if (time.length === 5) time = `${time}:00`;
  return { enabled: true, date: String(raw.date).slice(0, 10), time };
}

/**
 * Indica si el movimiento cae dentro del periodo en el que se deben mostrar inconsistencias.
 */
export function movementIsOnOrAfterInconsistencyCutoff(movement, countFrom) {
  if (!countFrom?.enabled || !countFrom?.date) return true;
  const cutoffTime = countFrom.time || '00:00:00';
  const cutoff = new Date(`${countFrom.date}T${cutoffTime.length === 5 ? `${cutoffTime}:00` : cutoffTime}`);
  const movDate = String(movement?.movement_date ?? '').slice(0, 10);
  const movTimeRaw = movement?.movement_time ? String(movement.movement_time) : '00:00:00';
  const movTime = movTimeRaw.length === 5 ? `${movTimeRaw}:00` : movTimeRaw;
  const mov = new Date(`${movDate}T${movTime}`);
  if (Number.isNaN(cutoff.getTime()) || Number.isNaN(mov.getTime())) return true;
  return mov >= cutoff;
}

/**
 * Stock en cero con valor residual menor a S/ 0.05 (positivo o negativo).
 */
export function isZeroStockValueDust(stock, totalValue) {
  if (Math.abs(q3(stock)) > TOLERANCE_ZERO) return false;
  return Math.abs(Number(totalValue || 0)) < ZERO_STOCK_DUST_TOLERANCE;
}

export function shouldUseTemporaryKardexCost(stockBefore, stockAfter) {
  const before = q3(stockBefore);
  const after = q3(stockAfter);
  return before <= TOLERANCE_ZERO || after < -TOLERANCE_ZERO;
}

/**
 * @param {object[]} movements
 * @param {number} [beforeIndex]
 */
export function findLastKardexEntradaUnitPrice(movements, beforeIndex = movements?.length ?? 0) {
  for (let i = beforeIndex - 1; i >= 0; i -= 1) {
    const movement = movements[i];
    if (movement?.movement_type !== 'entrada') continue;
    const unit = q4(movement.unit_price);
    if (unit <= TOLERANCE_ZERO) continue;
    return {
      unit_price: unit,
      source_movement_id: movement.id,
      source_guide_code: movement.guide_number || movement.entry_guide?.code || null,
      source_event: movement.kardex_event,
    };
  }
  return null;
}

export function resolveTemporaryKardexSalidaPricing(quantity, lastEntrada) {
  const qty = q3(quantity);
  if (!lastEntrada || q4(lastEntrada.unit_price) <= TOLERANCE_ZERO) {
    return {
      unit_price: 0,
      total: 0,
      operation_data: {
        temporary_cost: true,
        temporary_cost_source: 'none',
        temporary_cost_sin_referencia: true,
        approximate: true,
      },
    };
  }
  const unit = q4(lastEntrada.unit_price);
  return {
    unit_price: unit,
    total: q4(qty * unit),
    operation_data: {
      temporary_cost: true,
      temporary_cost_source: 'last_entrada',
      source_movement_id: lastEntrada.source_movement_id,
      source_guide_code: lastEntrada.source_guide_code,
      source_event: lastEntrada.source_event,
      source_unit_price: unit,
      approximate: true,
    },
  };
}

export const TEMPORARY_COST_INCONSISTENCY_TYPES = new Set([
  'costo_temporal_aplicado',
  'costo_temporal_sin_referencia',
  'costo_temporal_heredado_traspaso',
]);

export function movementHasTemporaryCost(operationData) {
  return Boolean(operationData?.temporary_cost);
}

export function getTemporaryCostInconsistencies(movement) {
  return (movement?.inconsistencies || []).filter((item) =>
    TEMPORARY_COST_INCONSISTENCY_TYPES.has(item.type)
  );
}

export function isTemporaryCostMovement(movement, options = {}) {
  const { enabled = true } = options;

  if (movementHasTemporaryCost(movement?.operation_data)) return true;

  if (!enabled) return false;

  if (movement?.movement_type === 'salida') {
    const stockBefore = movement.stock_before ?? movement.stockBefore ?? 0;
    const stockAfter = movement.stock_after ?? movement.stockAfter ?? 0;
    return shouldUseTemporaryKardexCost(stockBefore, stockAfter);
  }

  return false;
}

export function isSalidaTemporaryCost(movement, options = {}) {
  return movement?.movement_type === 'salida' && isTemporaryCostMovement(movement, options);
}

export function temporaryCostDisplayLabel(movement) {
  const op = movement?.operation_data;
  if (op?.temporary_cost_sin_referencia) return 'Sin referencia';
  if (op?.inherited_from_transfer) {
    return `Heredado ${op.transfer_guide_code || 'GT'}`;
  }
  if (op?.temporary_cost && op?.source_guide_code) return `Ref. ${op.source_guide_code}`;
  if (op?.temporary_cost) return 'Aproximado';
  const inc = getTemporaryCostInconsistencies(movement)[0];
  if (!inc) return 'Temporal';
  if (inc.type === 'costo_temporal_sin_referencia') return 'Sin referencia';
  if (inc.type === 'costo_temporal_heredado_traspaso') return 'Heredado GT';
  return 'Aproximado';
}

export function temporaryCostTooltipForMovement(movement) {
  if (movementHasTemporaryCost(movement?.operation_data)) {
    return temporaryCostTooltip(movement.operation_data);
  }
  const inc = getTemporaryCostInconsistencies(movement)[0];
  return inc?.message || 'Costo temporal aproximado por stock negativo.';
}

export function temporaryCostTooltip(operationData) {
  if (!movementHasTemporaryCost(operationData)) return '';
  if (operationData?.temporary_cost_sin_referencia) {
    return 'Costo temporal sin referencia de ingreso (valor aproximado).';
  }
  if (operationData?.inherited_from_transfer) {
    return `Costo temporal heredado del traspaso ${operationData.transfer_guide_code || ''} (aproximado).`;
  }
  const source = operationData?.source_guide_code ? ` (${operationData.source_guide_code})` : '';
  return `Costo temporal desde último ingreso${source}. Valor aproximado.`;
}

function detectTemporaryCostInconsistencies(movement, stockBeforeVal) {
  const items = [];
  const op = movement.operation_data;
  const stockAfter = movement.stock_after ?? movement.stockAfter;

  if (op?.temporary_cost_sin_referencia) {
    items.push({
      type: 'costo_temporal_sin_referencia',
      message: 'Stock negativo sin ingreso previo con costo para estimar la salida.',
    });
    return items;
  }

  if (op?.inherited_from_transfer) {
    items.push({
      type: 'costo_temporal_heredado_traspaso',
      message: `Recepción con costo temporal heredado del traspaso ${op.transfer_guide_code || op.source_guide_code || ''}.`,
    });
    return items;
  }

  if (op?.temporary_cost) {
    const source = op.source_guide_code ? ` (${op.source_guide_code})` : '';
    items.push({
      type: 'costo_temporal_aplicado',
      message: `Costo temporal S/ ${q4(op.source_unit_price ?? movement.unit_price).toFixed(4)} desde último ingreso${source}. Requiere ingreso real para confirmar.`,
    });
    return items;
  }

  if (
    movement.movement_type === 'salida'
    && shouldUseTemporaryKardexCost(stockBeforeVal, stockAfter ?? 0)
  ) {
    if (q4(movement.unit_price) <= TOLERANCE_ZERO) {
      items.push({
        type: 'costo_temporal_sin_referencia',
        message: 'Salida con stock negativo sin costo temporal asignado.',
      });
    } else {
      items.push({
        type: 'costo_temporal_aplicado',
        message: 'Salida con stock negativo o sin stock previo: costo aproximado desde último ingreso.',
      });
    }
  }

  return items;
}

/**
 * Detecta inconsistencias usando los mismos criterios que el backend.
 * Preferir average_cost_before / stock_after del API cuando existan.
 */
export function detectKardexInconsistencies(movement, { product, stockBefore, countFrom } = {}) {
  if (!movementIsOnOrAfterInconsistencyCutoff(movement, countFrom)) {
    return [];
  }

  const inconsistencies = [];
  const stockBeforeVal = stockBefore ?? movement.stock_before ?? 0;

  if (movement.cost_mismatch_alert?.has_mismatch) {
    inconsistencies.push({
      type: 'costo_recepcion',
      message:
        movement.cost_mismatch_alert.message ||
        `El costo unitario de recepción (S/ ${q4(movement.cost_mismatch_alert.reception_unit_price).toFixed(4)}) no coincide con el costo de la guía de traspaso ${movement.cost_mismatch_alert.transfer_guide_code} (S/ ${q4(movement.cost_mismatch_alert.transfer_unit_price).toFixed(4)})`,
    });
  }

  if (movement.movement_type === 'salida' && stockBeforeVal > 0) {
    const expectedUnit = q4(movement.average_cost_before ?? 0);
    const actualUnit = q4(movement.unit_price);
    if (actualUnit !== expectedUnit) {
      inconsistencies.push({
        type: 'costo_salida',
        message: `Costo unitario (S/ ${actualUnit.toFixed(4)}) no coincide con costo promedio anterior (S/ ${expectedUnit.toFixed(4)})`,
      });
    }
  }

  if (
    movement.movement_type === 'entrada'
    && q3(movement.quantity) > TOLERANCE_ZERO
    && movement.kardex_event !== 'transferencia_gratuita'
    && q4(movement.unit_price) <= TOLERANCE_ZERO
  ) {
    inconsistencies.push({
      type: 'entrada_costo_cero',
      message: 'Entrada con costo cero (no es transferencia gratuita)',
    });
  }

  inconsistencies.push(...detectTemporaryCostInconsistencies(movement, stockBeforeVal));

  const expectedTotal = kardexLineTotal(movement.quantity, movement.unit_price);
  if (!isKardexLineTotalConsistent(movement.quantity, movement.unit_price, movement.total)) {
    inconsistencies.push({
      type: 'total_inconsistente',
      message: `Total inconsistente: S/ ${q4(movement.total).toFixed(4)} ≠ cantidad × costo (S/ ${expectedTotal.toFixed(4)})`,
    });
  }

  const stockAfter = movement.stock_after ?? movement.stockAfter;
  const totalValueAfter = movement.total_value_after ?? movement.totalValueAfter;

  if (stockAfter != null && stockAfter < -TOLERANCE_ZERO) {
    inconsistencies.push({
      type: 'stock_negativo',
      message: `Stock negativo: ${q3(stockAfter).toFixed(3)} ${product?.unit_of_measure || ''}`,
    });
  }

  if (
    totalValueAfter != null
    && totalValueAfter < -TOLERANCE_ZERO
    && !isZeroStockValueDust(stockAfter, totalValueAfter)
  ) {
    inconsistencies.push({
      type: 'valor_negativo',
      message: `Valor total negativo: S/ ${q4(totalValueAfter).toFixed(4)}`,
    });
  }

  return inconsistencies;
}

/**
 * Bloquea recálculo desde el modal solo cuando el costo viene de un documento
 * (factura/boleta) sin guía asociada. Las recepciones de traspaso sí se pueden recalcular.
 */
export function blocksKardexRecalculateFromModal(movement, inconsistencies = []) {
  if (!movement) return true;

  const hasCostIssue = inconsistencies.some(
    (inc) => inc.type === 'costo_salida' || inc.type === 'costo_recepcion'
  );
  if (!hasCostIssue) return false;

  if (movement.entry_guide_item_id) return false;
  if (movement.entry_guide_code) return false;
  if (movement.cost_mismatch_alert?.has_mismatch) return false;

  if (movement.document?.code || movement.document?.series) return true;

  return false;
}

/**
 * Recorre movimientos aplicando q3/q4 como el backend (para recálculo en pantalla).
 */
export function foldKardexMovements(movements, initialStock = 0, initialTotalValue = 0, adjustMovement) {
  let stock = q3(initialStock);
  let totalValue = q4(initialTotalValue);
  let lastAverageCost = kardexAverageCost(stock, totalValue);
  const result = [];

  const list = movements || [];

  for (let index = 0; index < list.length; index += 1) {
    const movement = list[index];
    const stockBefore = stock;
    const totalValueBefore = totalValue;
    const lastAverageCostBefore = lastAverageCost;
    const averageCostBefore = kardexAverageCost(stockBefore, totalValueBefore);
    if (averageCostBefore > 0) {
      lastAverageCost = averageCostBefore;
    }

    let unitPrice = movement.unit_price;
    let total = movement.total;
    let operationData = movement.operation_data;

    const stockAfterPreview = q3(stockBefore - movement.quantity);
    if (
      movement.movement_type === 'salida'
      && shouldUseTemporaryKardexCost(stockBefore, stockAfterPreview)
    ) {
      const resolved = resolveTemporaryKardexSalidaPricing(
        movement.quantity,
        findLastKardexEntradaUnitPrice(list, index)
      );
      unitPrice = resolved.unit_price;
      total = resolved.total;
      operationData = resolved.operation_data;
    }

    if (typeof adjustMovement === 'function') {
      const adjusted = adjustMovement(movement, {
        stockBefore,
        totalValueBefore,
        averageCostBefore,
        lastAverageCost: lastAverageCostBefore,
      });
      if (adjusted) {
        unitPrice = adjusted.unit_price ?? unitPrice;
        total = adjusted.total ?? total;
      }
    }

    if (movement.movement_type === 'salida') {
      stock = q3(stock - movement.quantity);
      totalValue = q4(totalValue - total);
    } else {
      stock = q3(stock + movement.quantity);
      totalValue = q4(totalValue + total);
    }

    const averageCostAfter = resolveKardexAverageCostAfter(movement, stock, totalValue);
    totalValue = syncKardexTotalValueAfterCostAdjustment(movement, stock, averageCostAfter, totalValue);
    if (averageCostAfter > 0) {
      lastAverageCost = averageCostAfter;
    }

    result.push({
      ...movement,
      unit_price: unitPrice,
      total: q4(total),
      operation_data: operationData,
      stock_before: stockBefore,
      stock_after: stock,
      total_value_before: q4(totalValueBefore),
      total_value_after: q4(totalValue),
      average_cost_before: averageCostBefore,
      average_cost_after: averageCostAfter,
    });
  }

  return result;
}
